import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import * as flags from "country-flag-icons/react/3x2";
import { REST_API_URL } from "../../api/config";

import {
  FiPlus,
  FiUser,
  FiTrash2,
  FiChevronDown,
  FiCheck,
  FiMapPin,
  FiSave,
  FiUsers,
  FiMail,
  FiGlobe,
  FiSearch,
} from "react-icons/fi";
import {  MdSatellite, MdMap, MdMyLocation } from "react-icons/md";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";

const BASE_URL = REST_API_URL
const USER_INFO_URL = `${BASE_URL}/auth/userinfo/`;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone; 
  }
  
  return headers;
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const customIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4F46E5" width="36" height="36">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  shadowSize: [41, 41],
  shadowAnchor: [12, 41],
});

function MapUpdater({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);

  return null;
}

function SearchControl({ onSearch, onUseCurrentLocation }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}&limit=5`
      );

      if (!response.ok) {
        throw new Error("Search failed");
      }

      const results = await response.json();

      if (results.length > 0) {
        const firstResult = results[0];
        const position = {
          lat: parseFloat(firstResult.lat),
          lng: parseFloat(firstResult.lon),
        };

        onSearch(position, firstResult);
        setSearchQuery(firstResult.display_name);
      } else {
        toast.error("Location not found. Try a different address.");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          onUseCurrentLocation(userPosition);
          toast.success("Current location detected!");
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error(
            "Unable to get your location. Please allow location access."
          );
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Search for address, city, or landmark..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:ring-opacity-50 transition-all bg-white"
          />
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSearch}
          disabled={isSearching}
          className="h-10 px-4 bg-indigo-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {isSearching ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <>
              <FiSearch className="w-4 h-4" />
              <span>Search</span>
            </>
          )}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleUseCurrentLocation}
          className="h-10 px-3 bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-200 transition-colors"
          title="Use my current location"
        >
          <MdMyLocation className="w-4 h-4" />
        </motion.button>
      </div>
    </div>
  );
}

const fadeInUp = {
  initial: { opacity: 1, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 1, y: -20 },
};

const scaleIn = {
  initial: { opacity: 1, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 1, scale: 0.95 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

// Helper functions for phone number handling
const getNationalNumber = (fullPhoneNumber) => {
  if (!fullPhoneNumber) return "";
  const parsed = parsePhoneNumberFromString(fullPhoneNumber);
  if (parsed) {
    return parsed.nationalNumber;
  }
  return fullPhoneNumber;
};

const getFullPhoneNumber = (nationalNumber, countryCode) => {
  if (!nationalNumber) return "";
  return `${countryCode}${nationalNumber}`;
};

export function UserInformationCard() {
  const [userType, setUserType] = useState("individual");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // Now stores only national number
  const [addressDetails, setAddressDetails] = useState({
    country: "",
    city: "",
    address: "",
    state: "",
    postcode: "",
    fullAddress: "",
  });
  const [position, setPosition] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [mapStyle, setMapStyle] = useState("light");

  const [companyName, setCompanyName] = useState("");

  const [workers, setWorkers] = useState([]);
  const [workPlaces, setWorkPlaces] = useState([]);
  const [selectedWorkPlaces, setSelectedWorkPlaces] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("AM"); // Default to Armenia
  const [countryCode, setCountryCode] = useState("+374");
  const [openCountries, setOpenCountries] = useState(false);

  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);

  const [showAddWorkersModal, setShowAddWorkersModal] = useState(false);
  const [workerEmails, setWorkerEmails] = useState([]);
  const [workerEmailInput, setWorkerEmailInput] = useState("");
  const [loadingAddWorkers, setLoadingAddWorkers] = useState(false);
  const [workersDropdownOpen, setWorkersDropdownOpen] = useState(false);
  const [notAvailableEmails, setNotAvailableEmails] = useState([]);

  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [isDeletingWorker, setIsDeletingWorker] = useState(false);

  const [isGeocoding, setIsGeocoding] = useState(false);

  const countryRef = useRef(null);
  const currencyRef = useRef(null);

  const mapStyles = {
    light: {
      url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      icon: <MdMap className="w-4 h-4" />,
    },
    dark: {
      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      icon: <FiGlobe className="w-4 h-4" />,
    },
    satellite: {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      icon: <MdSatellite className="w-4 h-4" />,
    },
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setOpenCountries(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target)) {
        setOpenCurrency(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    (async () => {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
      
      const headers = buildHeaders(token);

      const res = await fetch(USER_INFO_URL, { headers });
      const data = await res.json();

      setUserType(data.user?.status || "individual");

      setFullName(
        `${data.user.first_name || ""} ${data.user.last_name || ""}`.trim()
      );
      setEmail(data.user.email || "");
      
      // Fix: Store only national number without country code
      const nationalNumber = getNationalNumber(data.phone_number || "");
      setPhone(nationalNumber);
      
      // Detect country from phone number
      if (data.phone_number) {
        const parsed = parsePhoneNumberFromString(data.phone_number);
        if (parsed && parsed.country) {
          setSelectedCountry(parsed.country);
          setCountryCode("+" + getCountryCallingCode(parsed.country));
        }
      }

      setCompanyName(data.user?.company_name || "");

      if (data.address_url?.includes("?q=")) {
        const coords = data.address_url.split("?q=")[1].split(",");
        const initialPosition = {
          lat: parseFloat(coords[0]),
          lng: parseFloat(coords[1]),
        };
        setPosition(initialPosition);
        await fetchAddressDetails(initialPosition);
      } else {
        const defaultPosition = { lat: 48.8566, lng: 2.3522 };
        setPosition(defaultPosition);
        await fetchAddressDetails(defaultPosition);
      }

      if (data.currency) setCurrency(data.currency);

      if (Array.isArray(data.work_place)) {
        setWorkPlaces(data.work_place);
        setSelectedWorkPlaces(data.work_place.map((p) => p.id));
      }

      if (data.user?.status === "salon" && Array.isArray(data.workers)) {
        setWorkers(data.workers);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingCurrency(true);
      try {
        const token =
          localStorage.getItem("accessToken") ||
          sessionStorage.getItem("accessToken") ||
          "";
        
        const headers = buildHeaders(token);

        const res = await fetch(`${BASE_URL}/service/currencies/`, { headers });

        const data = await res.json();

        setCurrencies(
          data.map((item) => ({
            code: item.short_name,
            name: item.short_name,
            flag: item.icon || "",
          }))
        );

        if (!currency && data.length > 0) setCurrency(data[0].short_name);
      } finally {
        setLoadingCurrency(false);
      }
    })();
  }, []);

  const fetchAddressDetails = async (pos) => {
    if (!pos) return;

    setIsGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${pos.lat}&lon=${pos.lng}&format=json&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.address) {
        const addr = data.address;
        setAddressDetails({
          country: addr.country || "",
          city: addr.city || addr.town || addr.village || "",
          address:
            addr.road || addr.house_number
              ? `${addr.road || ""} ${addr.house_number || ""}`.trim()
              : "",
          state: addr.state || addr.county || "",
          postcode: addr.postcode || "",
          fullAddress: data.display_name || "",
        });
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    } finally {
      setIsGeocoding(false);
    }
  };

  useEffect(() => {
    if (position) {
      fetchAddressDetails(position);
    }
  }, [position]);

  const handleSearch = async (newPosition, searchResult) => {
    setPosition(newPosition);

    if (searchResult) {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${newPosition.lat}&lon=${newPosition.lng}&format=json&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.address) {
        const addr = data.address;
        setAddressDetails({
          country: addr.country || "",
          city: addr.city || addr.town || addr.village || "",
          address:
            addr.road || addr.house_number
              ? `${addr.road || ""} ${addr.house_number || ""}`.trim()
              : "",
          state: addr.state || addr.county || "",
          postcode: addr.postcode || "",
          fullAddress: data.display_name || "",
        });
      }
    }

    toast.success("Location found!");
  };

  const handleUseCurrentLocation = (userPosition) => {
    setPosition(userPosition);
    fetchAddressDetails(userPosition);
  };

  const save = async () => {
    setIsSaving(true);
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!position) {
      toast.error("Please select your location on the map.");
      setIsSaving(false);
      return;
    }

    // Fix: Build full phone number with country code for API
    const fullPhoneNumber = getFullPhoneNumber(phone, countryCode);

    const body = {
      full_name: fullName,
      company_name: userType === "salon" ? companyName : "",
      phone_number: fullPhoneNumber,
      city: addressDetails.city,
      address: addressDetails.fullAddress,
      address_url: `https://www.google.com/maps?q=${position.lat},${position.lng}`,
      business_description: "",
      currency: currency,
      work_place_ids: userType === "individual" ? selectedWorkPlaces : [],
    };

    try {
      const headers = buildHeaders(token);

      const res = await fetch(USER_INFO_URL, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data && typeof data === "object") {
          Object.keys(data).forEach((key) => {
            if (Array.isArray(data[key])) {
              data[key].forEach((err) => toast.error(`${key}: ${err}`));
            } else if (typeof data[key] === "string") {
              toast.error(`${key}: ${data[key]}`);
            }
          });
        } else {
          toast.error(data?.detail || data?.message || "Something went wrong");
        }
        return;
      }

      toast.success("Profile updated successfully!");

      const refreshHeaders = buildHeaders(token);
      const refresh = await fetch(USER_INFO_URL, { headers: refreshHeaders });
      const freshData = await refresh.json();

      setFullName(
        freshData.user?.full_name ||
          `${freshData.user?.first_name || ""} ${
            freshData.user?.last_name || ""
          }`.trim()
      );
      setEmail(freshData.user?.email || "");
      
      // Update phone with national number only
      const updatedNationalNumber = getNationalNumber(freshData.phone_number || "");
      setPhone(updatedNationalNumber);
      
      setCompanyName(freshData.user?.company_name || "");
      setCurrency(freshData.currency || "");

      if (Array.isArray(freshData.work_place)) {
        setWorkPlaces(freshData.work_place);
        setSelectedWorkPlaces(freshData.work_place.map((p) => p.id));
      }

      if (userType === "salon" && Array.isArray(freshData.workers)) {
        setWorkers(freshData.workers);
      }

      window.dispatchEvent(new Event("userUpdated"));
    } catch (err) {
      console.error(err);
      toast.error("Network error. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteWorker = async () => {
    if (!workerToDelete) return;
    setIsDeletingWorker(true);
    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        "";
      const headers = buildHeaders(token);
      const res = await fetch(`${BASE_URL}/auth/delete-worker/${workerToDelete.id}/`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || data?.message || "Failed to delete worker");
      }
      setWorkers((prev) => prev.filter((w) => w.id !== workerToDelete.id));
      toast.success("Worker removed successfully!");
      window.dispatchEvent(new Event("userUpdated"));
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to remove worker");
    } finally {
      setIsDeletingWorker(false);
      setWorkerToDelete(null);
    }
  };

  const handleSaveWorkers = async () => {
    if (workerEmails.length === 0) {
      toast.error("Please add at least one worker email");
      return;
    }

    setLoadingAddWorkers(true);

    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        "";
      
      const headers = buildHeaders(token);

      const currentUserRes = await fetch(USER_INFO_URL, { headers });
      const currentUserData = await currentUserRes.json();

      // Build full phone number with country code for API
      const fullPhoneNumber = getFullPhoneNumber(phone, countryCode);

      const body = {
        full_name: fullName,
        company_name: companyName || currentUserData.user?.company_name || "",
        phone_number: fullPhoneNumber || currentUserData.phone_number || "",
        city: addressDetails.city || currentUserData.city || "",
        address: addressDetails.fullAddress || currentUserData.address || "",
        address_url: position
          ? `https://www.google.com/maps?q=${position.lat},${position.lng}`
          : currentUserData.address_url || "",
        business_description: currentUserData.business_description || "",
        currency: currency || currentUserData.currency || "",
        work_place_ids: selectedWorkPlaces,
        worker_emails: workerEmails,
      };



      const res = await fetch(USER_INFO_URL, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });

      const resData = await res.json();

      if (!res.ok) {
        throw new Error(
          resData.detail ||
            resData.message ||
            JSON.stringify(resData) ||
            "Failed to add workers"
        );
      }

      // Check which emails are not registered in the system
      const unavailable = Array.isArray(resData.not_available_emails)
        ? resData.not_available_emails
        : [];

      if (unavailable.length > 0) {
        setNotAvailableEmails(unavailable);
        // Remove invalid emails from the list so user can fix and retry
        setWorkerEmails((prev) => prev.filter((e) => !unavailable.includes(e)));
        toast.error(`${unavailable.length} email(s) not found in the system`);
        // Keep modal open for correction
        return;
      }

      setNotAvailableEmails([]);

      const refreshHeaders = buildHeaders(token);
      const refreshRes = await fetch(USER_INFO_URL, { headers: refreshHeaders });
      const freshData = await refreshRes.json();

      if (Array.isArray(freshData.workers)) {
        setWorkers(freshData.workers);
      }

      if (freshData.user) {
        setFullName(
          `${freshData.user.first_name || ""} ${
            freshData.user.last_name || ""
          }`.trim()
        );
        setCompanyName(freshData.user.company_name || "");
      }

      // Update phone with national number only
      const updatedNationalNumber = getNationalNumber(freshData.phone_number || "");
      setPhone(updatedNationalNumber);

      setCurrency(freshData.currency || "");

      setShowAddWorkersModal(false);
      setWorkerEmails([]);
      setWorkerEmailInput("");

      toast.success("Workers invited successfully!");
      window.dispatchEvent(new Event("userUpdated"));
    } catch (error) {
      console.error("Error adding workers:", error);
      toast.error(error.message || "Failed to add workers");
    } finally {
      setLoadingAddWorkers(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 1, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 flex flex-col gap-8 border border-gray-100"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
          <FiUser className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-normal text-gray-800">
            User Information
          </h2>
        </div>
      </div>

      {userType === "worker" ? (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          <motion.div variants={fadeInUp} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Full name
            </label>
            <input
              className="h-14 w-full px-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Phone
            </label>
            <input
              className="h-14 w-full px-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
            />
          </motion.div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={save}
            disabled={isSaving}
            className="mt-2 w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FiSave className="w-5 h-5" />
                Save Changes
              </>
            )}
          </motion.button>
        </motion.div>
      ) : (
        <>
          <div className="flex gap-8">
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="flex flex-col gap-6 w-[50%] opacity-none"
            >
              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Full name
                </label>
                <input
                  className="h-14 w-full px-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    className="h-14 w-full pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 disabled"
                    value={email}
                    readOnly
                  />
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Phone Number
                </label>
                <div ref={countryRef} className="relative w-full">
                  <div
                    className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-white border border-gray-200 h-[44px] rounded-xl px-3 cursor-pointer shadow-sm hover:shadow transition-all duration-200 group"
                    onClick={() => setOpenCountries((v) => !v)}
                  >
                    {flags[selectedCountry]?.({
                      style: { width: 24, height: 18, borderRadius: 4 },
                    })}
                    <span className="text-sm font-medium text-gray-800">
                      {countryCode}
                    </span>
                    <FiChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                        openCountries ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  <input
                    className="h-14 pl-[8rem] pr-4 rounded-xl border border-gray-200 transition-all duration-300 w-full shadow-sm"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter phone number"
                  />

                  <AnimatePresence>
                    {openCountries && (
                      <motion.div
                        initial={{ opacity: 1, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 1, y: -10 }}
                        className="absolute z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-h-64 overflow-y-auto mt-2"
                      >
                        {getCountries().map((c) => (
                          <motion.div
                            key={c}
                            whileHover={{
                              backgroundColor: "rgba(99, 102, 241, 0.05)",
                            }}
                            className="flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 last:border-b-0"
                            onClick={() => {
                              setSelectedCountry(c);
                              setCountryCode("+" + getCountryCallingCode(c));
                              setOpenCountries(false);
                            }}
                          >
                            {flags[c]?.({
                              style: { width: 22, height: 16, borderRadius: 3 },
                            })}
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-800">
                                +{getCountryCallingCode(c)}
                              </div>
                              <div className="text-xs text-gray-500">{c}</div>
                            </div>
                            {selectedCountry === c && (
                              <FiCheck className="w-4 h-4 text-indigo-600" />
                            )}
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Currency
                </label>
                <div ref={currencyRef} className="relative">
                  <div
                    className="h-14 border border-gray-200 rounded-xl px-4 w-full flex items-center justify-between cursor-pointer bg-white shadow-sm hover:shadow transition-all duration-200"
                    onClick={() => setOpenCurrency((v) => !v)}
                  >
                    <span className="flex items-center gap-3">
                      {!loadingCurrency &&
                        currencies.find((c) => c.code === currency)?.flag && (
                          <img
                            alt={currency}
                            src={
                              currencies.find((c) => c.code === currency)?.flag
                            }
                            className="w-6 h-6 object-contain rounded-full"
                          />
                        )}
                      <span className="text-sm font-medium text-gray-800">
                        {loadingCurrency
                          ? "Loading..."
                          : currency || "Select currency"}
                      </span>
                    </span>
                    <FiChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                        openCurrency ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  <AnimatePresence>
                    {openCurrency && (
                      <motion.div
                        initial={{ opacity: 1, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 1, y: -10 }}
                        className="absolute z-50 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-h-64 overflow-y-auto"
                      >
                        {currencies.map((item) => (
                          <motion.div
                            key={item.code}
                            whileHover={{
                              backgroundColor: "rgba(99, 102, 241, 0.05)",
                            }}
                            className="flex items-center justify-between px-4 py-3 cursor-pointer border-b border-gray-50 last:border-b-0"
                            onClick={() => {
                              setCurrency(item.code);
                              setOpenCurrency(false);
                            }}
                          >
                            <span className="flex items-center gap-3">
                              {item.flag && (
                                <img
                                  src={item.flag}
                                  alt={item.name}
                                  className="w-6 h-6 object-contain rounded-full"
                                />
                              )}
                              <span className="text-sm font-medium text-gray-800">
                                {item.name}
                              </span>
                            </span>

                            {currency === item.code && (
                              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                                <FiCheck className="w-3 h-3 text-indigo-600" />
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>

              {userType === "salon" && (
                <motion.div variants={fadeInUp} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Company Name
                  </label>
                  <input
                    className="h-14 w-full px-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                  />
                </motion.div>
              )}

              {userType === "salon" && (
                <motion.div
                  variants={fadeInUp}
                  className="space-y-3 rounded-xl"
                >
                  <label className="font-semibold text-sm text-gray-700 uppercase tracking-wide">
                    Workers
                  </label>

                  {workers.length === 0 ? (
                    <>
                      <div className="h-12 border-2 border-dashed border-gray-300 rounded-xl px-4 flex items-center justify-center bg-gray-50">
                        <span className="text-gray-400 italic">
                          No workers added yet
                        </span>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={() => setShowAddWorkersModal(true)}
                      >
                        <FiPlus className="w-5 h-5" />
                        Add Worker
                      </motion.button>
                    </>
                  ) : (
                    <>
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        onClick={() => setWorkersDropdownOpen((prev) => !prev)}
                        className="h-12 border border-gray-200 rounded-xl px-4 flex items-center justify-between cursor-pointer bg-white shadow-sm hover:shadow transition-all duration-200"
                      >
                        <span className="text-gray-800 font-medium">
                          View Workers
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-600 rounded-full text-sm font-semibold">
                            {workers.length}
                          </span>
                          <FiChevronDown
                            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                              workersDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                      </motion.div>

                      <AnimatePresence>
                        {workersDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 1, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 1, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="bg-white border rounded-xl shadow-lg overflow-hidden"
                          >
                            <div className="max-h-48 overflow-y-auto">
                              {workers.map((w) => {
                                const avatar =
                                  w.image_url ||
                                  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

                                const registered = Boolean(w.full_name);

                                return (
                                  <motion.div
                                    key={w.id}
                                    whileHover={{
                                      backgroundColor:
                                        "rgba(99, 102, 241, 0.05)",
                                    }}
                                    className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="relative">
                                      <img
                                        src={avatar}
                                        alt="avatar"
                                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                                      />
                                      {registered && (
                                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                                      )}
                                    </div>

                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-800">
                                        {registered
                                          ? w.full_name
                                          : "Not registered"}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {w.email || w.phone_number || ""}
                                      </div>
                                    </div>

                                    <div
                                      className={`px-2 py-1 rounded text-xs font-medium ${
                                        registered
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {registered ? "Active" : "Pending"}
                                    </div>

                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setWorkerToDelete(w);
                                      }}
                                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors ml-1"
                                      title="Remove worker"
                                    >
                                      <FiTrash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                                    </motion.button>
                                  </motion.div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="p-2 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={() => setShowAddWorkersModal(true)}
                      >
                        <FiPlus className="w-5 h-5" />
                        Add More Workers
                      </motion.button>
                    </>
                  )}
                </motion.div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 1, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex flex-col w-[50%] h-full"
            >
              <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Location
              </label>

              <div className="mb-4">
                <SearchControl
                  onSearch={handleSearch}
                  onUseCurrentLocation={handleUseCurrentLocation}
                />
              </div>

              {position && (
                <motion.div
                  initial={{ opacity: 1, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="relative rounded-2xl overflow-hidden border-2 border-gray-200 shadow-2xl group"
                >
                  <div className="absolute top-4 left-4 z-10 flex gap-2">
                    <div className="flex gap-1 bg-white/95 backdrop-blur-sm rounded-xl p-1 border border-gray-200 shadow-sm">
                      {Object.keys(mapStyles).map((style) => (
                        <motion.button
                          key={style}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setMapStyle(style)}
                          className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${
                            mapStyle === style
                              ? "bg-indigo-600 text-white"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                          }`}
                        >
                          {mapStyles[style].icon}
                          <span className="capitalize">{style}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  <MapContainer
                    center={position}
                    zoom={14}
                    style={{
                      height: "350px",
                      width: "100%",
                    }}
                    className="rounded-2xl"
                    scrollWheelZoom={true}
                    attributionControl={false}
                  >
                    <TileLayer url={mapStyles[mapStyle].url} attribution="" />
                    <MapUpdater position={position} />
                    <Marker
                      position={position}
                      icon={customIcon}
                      draggable
                      eventHandlers={{
                        dragstart: () => {
                          document.body.style.cursor = "grabbing";
                        },
                        dragend: async (e) => {
                          document.body.style.cursor = "";
                          const newPosition = e.target.getLatLng();
                          setPosition(newPosition);
                          await fetchAddressDetails(newPosition);
                        },
                      }}
                    />
                  </MapContainer>

                  <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-xl border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <FiMapPin className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Drag marker to adjust location
                        </p>
                        <p className="text-xs text-gray-500">
                          Or click anywhere on map
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4 bg-black/80 text-white px-3 py-2 rounded-lg text-xs font-mono backdrop-blur-sm">
                    {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>

          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={save}
            disabled={isSaving}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                />
                <motion.span
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    repeat: Infinity,
                    duration: 1,
                    repeatType: "reverse",
                  }}
                >
                  Saving Changes...
                </motion.span>
              </>
            ) : (
              <>
                <motion.div
                  initial={{ rotate: 0 }}
                  whileHover={{ rotate: 15 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <FiSave className="w-6 h-6" />
                </motion.div>
                <span className="font-bold tracking-wide">
                  Save All Changes
                </span>
              </>
            )}
          </motion.button>
        </>
      )}

      <AnimatePresence>
        {workerToDelete && (
          <motion.div
            key="delete-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
            onClick={() => !isDeletingWorker && setWorkerToDelete(null)}
          >
            <motion.div
              key="delete-modal"
              initial={{ opacity: 1, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 1, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mx-auto mb-4">
                  <FiTrash2 className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                  Remove Worker
                </h3>
                <p className="text-sm text-gray-500 text-center">
                  Are you sure you want to remove{" "}
                  <span className="font-semibold text-gray-800">
                    {workerToDelete.full_name || workerToDelete.email}
                  </span>
                  {" "}from your team? This action cannot be undone.
                </p>
              </div>
              <div className="border-t border-gray-200 p-4 bg-gray-50 flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setWorkerToDelete(null)}
                  disabled={isDeletingWorker}
                  className="flex-1 h-11 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDeleteWorker}
                  disabled={isDeletingWorker}
                  className="flex-1 h-11 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingWorker ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <FiTrash2 className="w-4 h-4" />
                      Remove
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddWorkersModal && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
            onClick={() => {
              setShowAddWorkersModal(false);
              setNotAvailableEmails([]);
              setWorkerEmailInput("");
            }}
          >
            <motion.div
              key="modal"
              initial={{ opacity: 1, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 1, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Add Team Members
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      Invite workers to join your team
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setShowAddWorkersModal(false);
                      setNotAvailableEmails([]);
                      setWorkerEmailInput("");
                    }}
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                  >
                    ✕
                  </motion.button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    {(() => {
                      const val = workerEmailInput.toLowerCase().trim();
                      if (!val) return null;
                      if (workerEmails.includes(val))
                        return <p className="text-xs text-red-500 mt-0.5">This email is already in the invite list</p>;
                      if (workers.some((w) => w.email?.toLowerCase() === val))
                        return <p className="text-xs text-red-500 mt-0.5">This worker is already on your team</p>;
                      return null;
                    })()}
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="worker@example.com"
                        value={workerEmailInput}
                        onChange={(e) => {
                          setWorkerEmailInput(e.target.value.toLowerCase());
                          // Clear server-side errors when user starts typing a new email
                          if (notAvailableEmails.length > 0) setNotAvailableEmails([]);
                        }}
                        className={`flex-1 h-12 px-4 rounded-xl border focus:ring-2 focus:ring-opacity-50 transition-all ${
                          workerEmailInput.trim() && (
                            workerEmails.includes(workerEmailInput.toLowerCase().trim()) ||
                            workers.some((w) => w.email?.toLowerCase() === workerEmailInput.toLowerCase().trim())
                          )
                            ? "border-red-400 focus:border-red-400 focus:ring-red-200"
                            : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-200"
                        }`}
                        onKeyPress={(e) => {
                          if (e.key !== "Enter") return;
                          const val = workerEmailInput.toLowerCase().trim();
                          if (!val) return;
                          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return toast.error("Invalid email");
                          if (workerEmails.includes(val)) return toast.error("Already in the invite list");
                          if (workers.some((w) => w.email?.toLowerCase() === val)) return toast.error("Already on your team");
                          setWorkerEmails([...workerEmails, val]);
                          setWorkerEmailInput("");
                        }}
                      />
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="h-12 px-4 bg-indigo-100 text-indigo-600 rounded-xl font-medium flex items-center gap-2"
                        onClick={() => {
                          const val = workerEmailInput.toLowerCase().trim();
                          if (!val) return toast.error("Enter email");
                          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
                            return toast.error("Invalid email");
                          if (workerEmails.includes(val))
                            return toast.error("Already in the invite list");
                          if (workers.some((w) => w.email?.toLowerCase() === val))
                            return toast.error("Already on your team");
                          setWorkerEmails([...workerEmails, val]);
                          setWorkerEmailInput("");
                          toast.success("Added!");
                        }}
                      >
                        <FiPlus size={18} />
                      </motion.button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        Invited Workers ({workerEmails.length})
                      </span>
                      {workerEmails.length > 0 && (
                        <button
                          onClick={() => setWorkerEmails([])}
                          className="text-sm text-red-500 hover:text-red-700"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      <AnimatePresence>
                        {notAvailableEmails.length > 0 && (
                          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-2">
                            <span className="text-red-500 text-xs mt-0.5">⚠</span>
                            <div>
                              <p className="text-xs font-semibold text-red-600 mb-1">Not registered in the system:</p>
                              {notAvailableEmails.map((e) => (
                                <p key={e} className="text-xs text-red-500">{e}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        {workerEmails.map((email) => (
                          <motion.div
                            key={email}
                            initial={{ opacity: 1, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 1, x: 20 }}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                <FiMail className="w-4 h-4 text-indigo-600" />
                              </div>
                              <span className="text-sm font-medium">
                                {email}
                              </span>
                            </div>
                            <button
                              onClick={() =>
                                setWorkerEmails(
                                  workerEmails.filter((e) => e !== email)
                                )
                              }
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <FiTrash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                            </button>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowAddWorkersModal(false);
                      setNotAvailableEmails([]);
                      setWorkerEmailInput("");
                    }}
                    className="flex-1 h-12 px-6 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                    disabled={loadingAddWorkers}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSaveWorkers}
                    disabled={loadingAddWorkers || workerEmails.length === 0}
                    className="flex-1 h-12 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loadingAddWorkers ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiUsers className="w-5 h-5" />
                        Send Invites
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}